/**
 * `async_select` primitive for the Monitor wizard.
 *
 * Single-value picker that fetches options via the
 * POST /v1/admin/import-capabilities/{skill}/{cap}/options endpoint, then
 * renders them as a **searchable, scrollable combobox** (a Popover with a
 * filter input and a capped-height scroll list). This matters for long async
 * lists — e.g. DataEQ's `brand_id`, which can hold hundreds of brands under an
 * account, and `account_code`. `brand_id` is filtered by the currently-selected
 * account via `context`.
 *
 * For static-options single-select, use SelectField. For multi-value async,
 * use MultiSelectField.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';

import { Label } from '../../../../ui/label';
import { Input } from '../../../../ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../ui/popover';
import { cn } from '../../../../../lib/utils';
import { useCapabilityOptions } from '../../../../../hooks/useImportCapabilities';
import type {
  CapabilityField,
  ImportCapabilityOption,
} from '../../../../../types/importCapabilities';

interface AsyncSelectFieldProps {
  field: CapabilityField;
  value: string | undefined;
  onChange: (value: string) => void;
  skillName: string;
  capabilityId: string;
  /** Partial wizard state forwarded as `context` to the resolver — lets
   *  fields like `brand_id` depend on `account_code`. */
  context: Record<string, unknown>;
}

const AsyncSelectField: React.FC<AsyncSelectFieldProps> = ({
  field,
  value,
  onChange,
  skillName,
  capabilityId,
  context,
}) => {
  const provider = field.options_provider;
  const usesAsync = Boolean(provider?.script || provider?.backend_tool);
  const labelId = `field-${field.id}-label`;
  const triggerId = `field-${field.id}`;
  const listboxId = `field-${field.id}-listbox`;
  const fieldLabel = field.label || field.id;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Index of the keyboard-highlighted option within `filtered`. Enables
  // ArrowUp/ArrowDown + Enter selection (the plain shadcn Select this replaced
  // gave that for free; a Popover + Input does not). -1 = nothing highlighted.
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const optionId = (i: number) => `${listboxId}-opt-${i}`;

  // Mirror MultiSelectField's pattern: useQuery (not useMutation) so React
  // handles the mount lifecycle correctly under React 18 StrictMode.
  const resolveQuery = useCapabilityOptions({
    skill_name: skillName,
    capability_id: capabilityId,
    field_id: field.id,
    context,
    // Plumb the field's declared deps so the cache key is scoped to only
    // the context entries this resolver actually consumes — see the hook
    // comment for why. Falls back to whole-context keying if absent.
    depends_on: provider?.depends_on,
    enabled: usesAsync,
  });

  // Memoised so its reference is stable across renders — otherwise the
  // `filtered` useMemo below would recompute every render (eslint deps).
  const options: ImportCapabilityOption[] = useMemo(
    () => (usesAsync ? resolveQuery.data?.options ?? [] : field.options ?? []),
    [usesAsync, resolveQuery.data?.options, field.options],
  );

  const selected = options.find((opt) => opt.value === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q),
    );
  }, [options, query]);

  // Keep the highlight in range as the filtered set changes (typing, opening).
  // Clamp to the first option so Enter always has a sane target.
  useEffect(() => {
    setActiveIndex((prev) => {
      if (filtered.length === 0) return -1;
      if (prev < 0 || prev >= filtered.length) return 0;
      return prev;
    });
  }, [filtered, open]);

  // Scroll the highlighted option into view when navigating by keyboard.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const node = listRef.current?.querySelector<HTMLElement>(
      `#${CSS.escape(optionId(activeIndex))}`,
    );
    node?.scrollIntoView({ block: 'nearest' });
    // optionId is stable for a given listboxId; deps intentionally scope to the
    // active index + open state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, open]);

  const select = (next: string) => {
    onChange(next);
    setOpen(false);
    setQuery('');
  };

  const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => (filtered.length === 0 ? -1 : (prev + 1) % filtered.length));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) =>
        filtered.length === 0 ? -1 : (prev - 1 + filtered.length) % filtered.length,
      );
    } else if (event.key === 'Enter') {
      // Only hijack Enter when a valid option is highlighted, so the key still
      // does nothing harmful on an empty result set.
      if (activeIndex >= 0 && activeIndex < filtered.length) {
        event.preventDefault();
        select(filtered[activeIndex].value);
      }
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <Label id={labelId} htmlFor={triggerId}>
        {fieldLabel}
        {field.required ? <span className="ml-1 text-red-500">*</span> : null}
      </Label>

      {usesAsync && resolveQuery.isPending ? (
        <p className="text-xs text-slate-500">Loading {fieldLabel}…</p>
      ) : (
        <Popover
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            if (!next) setQuery('');
          }}
        >
          <PopoverTrigger asChild>
            <button
              id={triggerId}
              type="button"
              role="combobox"
              aria-labelledby={labelId}
              aria-controls={listboxId}
              aria-expanded={open}
              className={cn(
                'flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
                !selected && 'text-muted-foreground',
              )}
            >
              <span className="truncate">
                {selected ? selected.label : `Pick a ${fieldLabel}…`}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[--radix-popover-trigger-width] p-0"
            align="start"
          >
            <div className="border-b p-2">
              <Input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onSearchKeyDown}
                placeholder={`Search ${fieldLabel}…`}
                className="h-8"
                role="combobox"
                aria-expanded={open}
                aria-controls={listboxId}
                aria-autocomplete="list"
                aria-activedescendant={
                  activeIndex >= 0 && activeIndex < filtered.length
                    ? optionId(activeIndex)
                    : undefined
                }
                aria-label={`Search ${fieldLabel}`}
              />
            </div>
            <div ref={listRef} id={listboxId} className="max-h-64 overflow-y-auto p-1" role="listbox">
              {filtered.length === 0 ? (
                <p className="px-2 py-6 text-center text-xs text-muted-foreground">
                  No matches.
                </p>
              ) : (
                filtered.map((opt, i) => (
                  <button
                    key={opt.value}
                    id={optionId(i)}
                    type="button"
                    role="option"
                    aria-selected={opt.value === value}
                    onClick={() => select(opt.value)}
                    onMouseEnter={() => setActiveIndex(i)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground',
                      i === activeIndex && 'bg-accent text-accent-foreground',
                      opt.value === value && 'bg-accent/50',
                    )}
                  >
                    <Check
                      className={cn(
                        'h-4 w-4 shrink-0',
                        opt.value === value ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <span className="truncate">{opt.label}</span>
                  </button>
                ))
              )}
            </div>
          </PopoverContent>
        </Popover>
      )}

      {usesAsync && resolveQuery.isError ? (
        <p className="text-xs text-red-600">
          Could not load {fieldLabel}:{' '}
          {resolveQuery.error instanceof Error
            ? resolveQuery.error.message
            : 'unknown error'}
        </p>
      ) : null}

      {!resolveQuery.isPending && !resolveQuery.isError && options.length === 0 ? (
        <p className="text-xs text-slate-500">No options available.</p>
      ) : null}

      {field.description ? (
        <p className="text-xs text-slate-500">{field.description}</p>
      ) : null}
    </div>
  );
};

export default AsyncSelectField;
